// ============================================================
// TtrActionPanel — 3 액션 버튼 + face-up 슬롯 + 비공개 덱 + 서브 상태 UI
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §2-5 / §3-1
//   SEQ=10146 — IDLE / AWAIT_SECOND_CARD / AWAIT_TICKET_KEEP 분기 표시
// ============================================================
import React from 'react'
import { TtrFaceUpSlot, TtrSubPhase } from '../store/ticket'
import TtrCardIcon from './TtrCardIcon'

export interface TtrActionPanelProps {
  isMyTurn:         boolean
  subPhase:         TtrSubPhase
  faceUp:           TtrFaceUpSlot[]
  deckRemain:       number
  /** catch #19 후속 (SEQ=10204 STEP=2, 2026-05-26): 비공개 덱 버튼 가드 — deckRemain=0이라도 discardRemain>0이면 서버 재셔플 발동되도록 클릭 허용. */
  discardRemain:    number
  ticketDeckRemain: number
  /** SEQ=10199 STEP=1: 한 턴 1액션 가드 — 현 턴 액션 수행 여부 (true=수행 완료, false=미수행). */
  actionDoneThisTurn: boolean
  onDrawFromFaceUp: (slotIndex: number) => void
  onDrawFromDeck:   () => void
  onDrawTickets:    () => void
  onEndTurn:        () => void
  /** SEQ=10211 STEP=17: button disabled 사유 토스트 트리거 (가설 B - 사용자 catch "여전히 먹통" 진단) */
  onDisabledReason?: (reason: string) => void
}

const COLOR_CLASS_MAP: Record<string, string> = {
  RED:'red', ORANGE:'orange', YELLOW:'yellow', GREEN:'green',
  BLUE:'blue', PINK:'pink', WHITE:'white', BLACK:'black', WILD:'wild',
}

const TtrActionPanel: React.FC<TtrActionPanelProps> = ({
  isMyTurn, subPhase, faceUp, deckRemain, discardRemain, ticketDeckRemain, actionDoneThisTurn,
  onDrawFromFaceUp, onDrawFromDeck, onDrawTickets, onEndTurn, onDisabledReason,
}) => {
  // SEQ=10211 STEP=17 (가설 B fix): disabled prop 제거 + onClick 가드 + reason 토스트 안내.
  //   사용자가 button 클릭 시 즉시 disabled 사유 인지 (먹통 인지 회피).
  function notifyDisabled(reason: string) {
    if (onDisabledReason) onDisabledReason(reason)
    else console.warn('[TtrActionPanel] disabled reason:', reason)
  }
  function disabledReason(check: 'draw' | 'newAction' | 'endTurn'): string | null {
    if (!isMyTurn) return '상대 턴입니다'
    if (check === 'draw') {
      if (subPhase === 'AWAIT_SECOND_CARD') return null  // 2장째 허용
      if (subPhase === 'AWAIT_TICKET_KEEP') return '티켓 keep 모달 진행 중'
      if (actionDoneThisTurn) return '이 턴 액션 수행 완료 — 턴 종료 필요'
      return null
    }
    if (check === 'newAction') {
      if (subPhase !== 'IDLE') return '서브 상태 진행 중'
      if (actionDoneThisTurn) return '이 턴 액션 수행 완료 — 턴 종료 필요'
      return null
    }
    if (check === 'endTurn') {
      if (subPhase !== 'IDLE') return '서브 상태 진행 중 — 모달 완료 필요'
      if (!actionDoneThisTurn) return '액션 1회 수행 필요'
      return null
    }
    return null
  }
  // STEP=17: disabled 시각용 (button click은 허용, onClick 안에서 reason 토스트)
  const isSecondCard      = subPhase === 'AWAIT_SECOND_CARD'
  const canDrawCard       = isMyTurn && (
    (subPhase === 'IDLE' && !actionDoneThisTurn) ||
     subPhase === 'AWAIT_SECOND_CARD'
  )
  const canStartNewAction = isMyTurn && subPhase === 'IDLE' && !actionDoneThisTurn

  function handleFaceUpClick(slot: TtrFaceUpSlot) {
    const reason = disabledReason('draw')
    if (reason) return notifyDisabled(reason)
    if (isSecondCard && slot.cardColor === 'WILD') return notifyDisabled('2장째 와일드 카드 금지')
    onDrawFromFaceUp(slot.slotIndex)
  }
  function handleDeckClick() {
    const reason = disabledReason('draw')
    if (reason) return notifyDisabled(reason)
    if (deckRemain === 0 && discardRemain === 0) return notifyDisabled('덱·DISCARD 모두 비어있음')
    onDrawFromDeck()
  }
  function handleTicketsClick() {
    const reason = disabledReason('newAction')
    if (reason) return notifyDisabled(reason)
    if (ticketDeckRemain === 0) return notifyDisabled('티켓 덱 비어있음')
    onDrawTickets()
  }
  function handleEndTurnClick() {
    const reason = disabledReason('endTurn')
    if (reason) return notifyDisabled(reason)
    onEndTurn()
  }

  return (
    <section className="ttr-action-panel">
      <h3>액션 {!isMyTurn && <small>(상대 턴)</small>}</h3>

      <div className="ttr-subphase-hint">
        {subPhase === 'AWAIT_SECOND_CARD' && '두 번째 카드 선택 대기 (와일드 슬롯 불가)'}
        {subPhase === 'AWAIT_TICKET_KEEP' && '티켓 keep 선택 대기 — 모달 확인'}
        {subPhase === 'IDLE' && isMyTurn && '액션 선택'}
      </div>

      {/* Face-up 5장 — STEP=17 (disabled 제거 + onClick 가드 + reason 토스트) */}
      <div className="ttr-faceup-row">
        {faceUp.map(slot => {
          const isDisabledStyle = !canDrawCard || (isSecondCard && slot.cardColor === 'WILD')
          return (
            <button
              key={slot.slotIndex}
              className={'ttr-faceup-card ttr-color-' + (COLOR_CLASS_MAP[slot.cardColor] ?? 'gray') + (isDisabledStyle ? ' ttr-btn-disabled-style' : '')}
              onClick={() => handleFaceUpClick(slot)}
              title={slot.cardColor}
            >
              <TtrCardIcon color={slot.cardColor} className="ttr-faceup-icon" />
              <span className="ttr-faceup-label">{slot.cardColor}</span>
            </button>
          )
        })}
      </div>

      <div className="ttr-action-buttons">
        <button
          className={'ttr-action-btn ttr-action-deck' + (!canDrawCard || (deckRemain === 0 && discardRemain === 0) ? ' ttr-btn-disabled-style' : '')}
          onClick={handleDeckClick}
        >
          비공개 덱 ({deckRemain}{deckRemain === 0 && discardRemain > 0 ? ' · 재셔플 가능' : ''})
        </button>
        <button
          className={'ttr-action-btn ttr-action-tickets' + (!canStartNewAction || ticketDeckRemain === 0 ? ' ttr-btn-disabled-style' : '')}
          onClick={handleTicketsClick}
          title="티켓 3장 드로우 (keep 모달)"
        >
          티켓 드로우 ({ticketDeckRemain})
        </button>
        <button
          className={'ttr-action-btn ttr-action-end' + (!isMyTurn || !actionDoneThisTurn || subPhase !== 'IDLE' ? ' ttr-btn-disabled-style' : '')}
          onClick={handleEndTurnClick}
          title={!actionDoneThisTurn ? '액션 1회 수행 필요' : (subPhase !== 'IDLE' ? '서브 상태 진행 중' : '명시 턴 종료')}
        >
          턴 종료
        </button>
      </div>

      <p className="ttr-action-hint">
        노선 클레임은 보드의 노선을 클릭 → 카드 선택 모달.
      </p>
    </section>
  )
}

export default TtrActionPanel
